import React, { useState } from "react";
import ReactTable from "react-table";
import { Button, ButtonGroup } from "reactstrap";
import { CancelacionProceso } from "../../../types/CancelacionProceso";
import moment from "moment";
import permissionCodes from "../../../utils/permissionCodes";
import { userHasPermission } from "../../../utils/accessTree";
import BaseButtonWithIconAndTooltip from "../../../toolkit/baseButtonWithIconAndTooltip";
import {
  faEye,
  faQuestion,
  faUser,
  faRedo
} from "@fortawesome/free-solid-svg-icons";
import { formatIsoDate } from "../../helpers/formatters";

interface Props {
  data: CancelacionProceso[];
  loading: boolean;
  onVerIntentos: (idFactura: number) => void;
  onVerificarCancelacion: (uuidCancelacion: string) => void;
  onConfirmarCancelacion: (uuidCancelacion: string) => void;
  onReenviarSolicitud: (uuidCancelacion: string) => void;
  postingVerificarCancelacion: boolean;
  postingConfirmarCancelacion: boolean;
  postingReenviarSolicitudCancelacion: boolean;
}

const columnsFormatter = (
  onVerIntentos: (idFactura: number) => void,
  onVerificarCancelacion: (uuidCancelacion: string) => void,
  onConfirmarCancelacion: (uuidCancelacion: string) => void,
  onReenviarSolicitud: (uuidCancelacion: string) => void,
  postingVerificarCancelacion: boolean,
  postingConfirmarCancelacion: boolean,
  postingReenviarSolicitudCancelacion: boolean
) => {
  const {
    CONFIRMAR_CANCELACION_MANUALMENTE,
    REENVIAR_SOLICITUD_CANCELACION
  } = permissionCodes;

  return [
    {
      Header: "Factura",
      accessor: "folioFactura",
      headerClassName: "text-left",
      width: 100
    },
    {
      Header: "UUID Factura",
      accessor: "uuidFactura",
      headerClassName: "text-left",
      Cell: ({ value }) => <span>{value}</span>
    },
    {
      Header: "UUID Cancelación",
      accessor: "uuidCancelacion",
      headerClassName: "text-left",
      Cell: ({ value }) => <span>{value}</span>
    },
    {
      Header: "Fec. de Timbrado",
      accessor: "fechaTimbrado",
      headerClassName: "text-left",
      width: 150,
      Cell: ({ value }) => (value ? formatIsoDate(value) : "-")
    },
    {
      Header: "Fec. de Cancelación",
      accessor: "fechaCancelacion",
      headerClassName: "text-left",
      width: 150,
      Cell: ({ value }) => (value ? formatIsoDate(value) : "-")
    },
    {
      Header: "Venta Público",
      accessor: "ventaPublico",
      headerClassName: "text-left",
      width: 100,
      Cell: ({ value }) => <span>{value ? "Sí" : "No"}</span>
    },
    {
      Header: "Acciones",
      width: 150,
      Cell: ({ original }) => {
        /*
          Loading a nivel de fila, debe implementarse UseEffect si se requiere
          const disabled =
          postingVerificarCancelacion ||
          postingConfirmarCancelacion ||
          postingReenviarSolicitudCancelacion;*/
        const disabled = false;
        const color = disabled ? "#ccc" : "#000";

        return (
          <>
            <BaseButtonWithIconAndTooltip
              id={`ver${original.idFactura}`}
              icon={faEye}
              color={color}
              tooltipTitle={disabled ? "Espere un momento..." : "Ver intentos"}
              onClick={() => disabled || onVerIntentos(original.idFactura)}
            />
            <BaseButtonWithIconAndTooltip
              id={`verificar${original.idFactura}`}
              icon={faQuestion}
              color={color}
              tooltipTitle={
                disabled ? "Espere un momento..." : "Verificar cancelación"
              }
              onClick={() =>
                disabled || onVerificarCancelacion(original.uuidFactura)
              }
            />
            {userHasPermission(CONFIRMAR_CANCELACION_MANUALMENTE) && (
              <BaseButtonWithIconAndTooltip
                id={`confirmar${original.idFactura}`}
                icon={faUser}
                color={color}
                tooltipTitle={
                  disabled ? "Espere un momento..." : "Confirmar cancelación"
                }
                onClick={() =>
                  disabled || onConfirmarCancelacion(original.uuidFactura)
                }
              />
            )}
            {userHasPermission(REENVIAR_SOLICITUD_CANCELACION) && (
              <BaseButtonWithIconAndTooltip
                id={`reenviar${original.idFactura}`}
                icon={faRedo}
                color={color}
                tooltipTitle={
                  disabled
                    ? "Espere un momento..."
                    : "Reenviar solicitud de cancelación"
                }
                onClick={() =>
                  disabled || onReenviarSolicitud(original.uuidFactura)
                }
              />
            )}
          </>
        );
      }
    }
  ];
};

export const CancelacionesProcesoTable = React.memo<Props>(
  ({
    data,
    loading,
    onVerIntentos,
    onVerificarCancelacion,
    onConfirmarCancelacion,
    onReenviarSolicitud,
    postingVerificarCancelacion,
    postingConfirmarCancelacion,
    postingReenviarSolicitudCancelacion
  }) => {
    const [columns] = useState(() =>
      columnsFormatter(
        onVerIntentos,
        onVerificarCancelacion,
        onConfirmarCancelacion,
        onReenviarSolicitud,
        postingVerificarCancelacion,
        postingConfirmarCancelacion,
        postingReenviarSolicitudCancelacion
      )
    );

    return (
      <div className="mt-4">
        <ReactTable
          data={data || []}
          columns={columns}
          loading={loading}
          showPagination={false}
          pageSize={(data || []).length || 5}
          minRows={5}
          noDataText="No se encontraron cancelaciones en proceso"
          loadingText="Cargando..."
          className="-striped -highlight"
        />
      </div>
    );
  }
);
